import React, { useState } from "react";
import { Modal, Button, Radio, Tag, Space, message } from "antd";
import {
  DownloadOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  CheckCircleFilled,
  QrcodeOutlined
} from "@ant-design/icons";
import { downloadQrAsPng, downloadQrAsPdf } from "./QrCode";

const QrDownloadModal = ({
  open,
  visible,
  onClose,
  onCancel,
  qrCodeUrl,
  user = {},
  payId,
  downloadPng,
  downloadPdf,
  zIndex = 2000,
}) => {
  const isVisible = open !== undefined ? open : visible;
  const handleClose = onClose || onCancel;

  const [format, setFormat] = useState("png"); // "png" | "pdf"
  const [pdfSize, setPdfSize] = useState("a5"); // "a5" | "a6" | "a4"
  const [downloading, setDownloading] = useState(false);

  const displayName =
    user.business_name ||
    user.full_name ||
    [user.pronoun, user.first_name, user.middle_name, user.last_name]
      .filter(Boolean)
      .join(" ")
      .trim() ||
    "DuePay User";

  const isBusiness = !!user.business_name;

  const handleDownload = async () => {
    if (!qrCodeUrl || qrCodeUrl === "N/A") {
      message.error("QR Code URL is not available.");
      return;
    }

    try {
      setDownloading(true);
      if (format === "png") {
        if (downloadPng) {
          await downloadPng(pdfSize);
        } else {
          await downloadQrAsPng({ qrCodeUrl, user, payId, size: pdfSize });
        }
        message.success(`QR code downloaded as PNG (${pdfSize.toUpperCase()} • 300 DPI) successfully!`);
      } else {
        if (downloadPdf) {
          await downloadPdf(pdfSize);
        } else {
          await downloadQrAsPdf({ qrCodeUrl, user, payId, size: pdfSize });
        }
        message.success(`QR code downloaded as PDF (${pdfSize.toUpperCase()} • 300 DPI) successfully!`);
      }
      if (handleClose) handleClose();
    } catch (error) {
      console.error("QR Download Error:", error);
      message.error("Failed to download QR code. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Modal
      open={isVisible}
      visible={isVisible}
      onCancel={handleClose}
      footer={null}
      centered
      destroyOnClose
      width={560}
      zIndex={zIndex}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #0A3C8C, #0878FF)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              fontSize: 18,
            }}
          >
            <QrcodeOutlined />
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#0F172A" }}>
              Download QR Code
            </div>
            <div style={{ fontSize: 12, color: "#64748B", fontWeight: 400 }}>
              Print-ready 300 DPI high resolution for standees and digital use
            </div>
          </div>
        </div>
      }
    >
      <div style={{ padding: "8px 0" }}>
        {/* Account Info Banner */}
        <div
          style={{
            background: "#F8FAFC",
            border: "1px solid #E2E8F0",
            borderRadius: 10,
            padding: "12px 16px",
            marginBottom: 20,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontWeight: 600, fontSize: 14, color: "#1E293B" }}>
                {displayName}
              </span>
              <Tag color={isBusiness ? "blue" : "cyan"} style={{ margin: 0, borderRadius: 12, fontSize: 11 }}>
                {isBusiness ? "Business Account" : "Personal Account"}
              </Tag>
            </div>
            {payId && payId !== "N/A" && (
              <div style={{ fontSize: 12, color: "#64748B", marginTop: 4 }}>
                Pay ID: <span style={{ fontFamily: "monospace", fontWeight: 600 }}>{payId}</span>
              </div>
            )}
          </div>
        </div>

        {/* Section Title */}
        <div style={{ fontSize: 13, fontWeight: 600, color: "#334155", marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.5px" }}>
          Select Download Format
        </div>

        {/* Option 1: Download as PNG */}
        <div
          onClick={() => setFormat("png")}
          style={{
            border: format === "png" ? "2px solid #0878FF" : "1.5px solid #E2E8F0",
            background: format === "png" ? "#F0F7FF" : "#FFFFFF",
            borderRadius: 12,
            padding: "14px 16px",
            marginBottom: 12,
            cursor: "pointer",
            transition: "all 0.2s ease",
            display: "flex",
            alignItems: "flex-start",
            gap: 14,
            boxShadow: format === "png" ? "0 4px 12px rgba(8, 120, 255, 0.12)" : "none",
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: format === "png" ? "#0878FF" : "#F1F5F9",
              color: format === "png" ? "#FFFFFF" : "#64748B",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 20,
              flexShrink: 0,
            }}
          >
            <FileImageOutlined />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ fontWeight: 600, fontSize: 15, color: "#0F172A" }}>
                Download as PNG
              </div>
              <Tag color="blue" style={{ borderRadius: 6, fontSize: 11, margin: 0 }}>
                300 DPI • Ultra HD
              </Tag>
            </div>
            <div style={{ fontSize: 13, color: "#64748B", marginTop: 4, lineHeight: "1.4" }}>
              Print-ready 300 DPI image with embedded physical resolution metadata, suitable for digital displays, websites, or professional printing.
            </div>
          </div>
          <div style={{ paddingTop: 2 }}>
            <Radio checked={format === "png"} onChange={() => setFormat("png")} />
          </div>
        </div>

        {/* Option 2: Download as PDF */}
        <div
          onClick={() => setFormat("pdf")}
          style={{
            border: format === "pdf" ? "2px solid #0878FF" : "1.5px solid #E2E8F0",
            background: format === "pdf" ? "#F0F7FF" : "#FFFFFF",
            borderRadius: 12,
            padding: "14px 16px",
            marginBottom: 16,
            cursor: "pointer",
            transition: "all 0.2s ease",
            display: "flex",
            alignItems: "flex-start",
            gap: 14,
            boxShadow: format === "pdf" ? "0 4px 12px rgba(8, 120, 255, 0.12)" : "none",
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: format === "pdf" ? "#0878FF" : "#F1F5F9",
              color: format === "pdf" ? "#FFFFFF" : "#64748B",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 20,
              flexShrink: 0,
            }}
          >
            <FilePdfOutlined />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ fontWeight: 600, fontSize: 15, color: "#0F172A" }}>
                Download as PDF
              </div>
              <Tag color="geekblue" style={{ borderRadius: 6, fontSize: 11, margin: 0 }}>
                300 DPI Print-Ready
              </Tag>
            </div>
            <div style={{ fontSize: 13, color: "#64748B", marginTop: 4, lineHeight: "1.4" }}>
              Formatted for standard ISO paper sizes at 300 DPI. Perfect for countertop payment stands, flyers, and table tents.
            </div>
          </div>
          <div style={{ paddingTop: 2 }}>
            <Radio checked={format === "pdf"} onChange={() => setFormat("pdf")} />
          </div>
        </div>

        {/* Print Size Selection for both PNG & PDF */}
        <div
          style={{
            background: "#F8FAFC",
            border: "1px dashed #CBD5E1",
            borderRadius: 10,
            padding: "16px",
            marginBottom: 20,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#1E293B" }}>
              Choose Print Size:
            </span>
            <Tag color="cyan" style={{ margin: 0, fontSize: 11, fontWeight: 600, borderRadius: 10 }}>
              Calibrated to 300 DPI
            </Tag>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
            {/* A5 Size */}
            <div
              onClick={() => setPdfSize("a5")}
              style={{
                border: pdfSize === "a5" ? "2px solid #0878FF" : "1px solid #CBD5E1",
                background: pdfSize === "a5" ? "#FFFFFF" : "#F8FAFC",
                borderRadius: 8,
                padding: "10px",
                textAlign: "center",
                cursor: "pointer",
                transition: "all 0.2s ease",
                boxShadow: pdfSize === "a5" ? "0 2px 8px rgba(8, 120, 255, 0.15)" : "none",
              }}
            >
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 4 }}>
                <Tag color={pdfSize === "a5" ? "blue" : "default"} style={{ margin: 0, fontWeight: 700, fontSize: 13, borderRadius: 4 }}>
                  A5
                </Tag>
              </div>
              <div style={{ fontSize: 11, color: "#475569", fontWeight: 600 }}>
                148 × 210 mm
              </div>
              <div style={{ fontSize: 10, color: "#64748B", marginTop: 2 }}>
                1748 × 2480 px (300 DPI)
              </div>
              <div style={{ fontSize: 10, color: "#16A34A", marginTop: 4, fontWeight: 500 }}>
                ★ Standee (Recommended)
              </div>
            </div>

            {/* A6 Size */}
            <div
              onClick={() => setPdfSize("a6")}
              style={{
                border: pdfSize === "a6" ? "2px solid #0878FF" : "1px solid #CBD5E1",
                background: pdfSize === "a6" ? "#FFFFFF" : "#F8FAFC",
                borderRadius: 8,
                padding: "10px",
                textAlign: "center",
                cursor: "pointer",
                transition: "all 0.2s ease",
                boxShadow: pdfSize === "a6" ? "0 2px 8px rgba(8, 120, 255, 0.15)" : "none",
              }}
            >
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 4 }}>
                <Tag color={pdfSize === "a6" ? "blue" : "default"} style={{ margin: 0, fontWeight: 700, fontSize: 13, borderRadius: 4 }}>
                  A6
                </Tag>
              </div>
              <div style={{ fontSize: 11, color: "#475569", fontWeight: 600 }}>
                105 × 148 mm
              </div>
              <div style={{ fontSize: 10, color: "#64748B", marginTop: 2 }}>
                1240 × 1748 px (300 DPI)
              </div>
              <div style={{ fontSize: 10, color: "#64748B", marginTop: 4, fontWeight: 500 }}>
                Table Tent / Compact
              </div>
            </div>

            {/* A4 Size */}
            <div
              onClick={() => setPdfSize("a4")}
              style={{
                border: pdfSize === "a4" ? "2px solid #0878FF" : "1px solid #CBD5E1",
                background: pdfSize === "a4" ? "#FFFFFF" : "#F8FAFC",
                borderRadius: 8,
                padding: "10px",
                textAlign: "center",
                cursor: "pointer",
                transition: "all 0.2s ease",
                boxShadow: pdfSize === "a4" ? "0 2px 8px rgba(8, 120, 255, 0.15)" : "none",
              }}
            >
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 4 }}>
                <Tag color={pdfSize === "a4" ? "blue" : "default"} style={{ margin: 0, fontWeight: 700, fontSize: 13, borderRadius: 4 }}>
                  A4
                </Tag>
              </div>
              <div style={{ fontSize: 11, color: "#475569", fontWeight: 600 }}>
                210 × 297 mm
              </div>
              <div style={{ fontSize: 10, color: "#64748B", marginTop: 2 }}>
                2480 × 3508 px (300 DPI)
              </div>
              <div style={{ fontSize: 10, color: "#64748B", marginTop: 4, fontWeight: 500 }}>
                Full Page / Poster
              </div>
            </div>
          </div>
        </div>

        {/* Modal Actions */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 24, paddingTop: 16, borderTop: "1px solid #E2E8F0" }}>
          <Button onClick={handleClose} disabled={downloading}>
            Cancel
          </Button>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            loading={downloading}
            onClick={handleDownload}
            style={{
              background: "linear-gradient(135deg, #0A3C8C, #0878FF)",
              borderColor: "#0A3C8C",
              fontWeight: 600,
              minWidth: 160,
              height: 38,
              borderRadius: 6,
            }}
          >
            {format === "png"
              ? `Download PNG (${pdfSize.toUpperCase()} • 300 DPI)`
              : `Download PDF (${pdfSize.toUpperCase()} • 300 DPI)`}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default QrDownloadModal;
